/**
 * Visionary — product variant selection
 * Updates the selected variant, product media, price, availability, pickup information,
 * URL, product forms, and sticky add-to-cart controls without affecting other sections.
 */
'use strict';

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.sectionRoot = this.closest('[data-section-id]') || this.closest('section') || document;
    this.variantData = [];
    this.options = [];
    this.currentVariant = null;
    this.abortController = null;
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  connectedCallback() {
    this.variantData = this.getVariantData();
    this.updateOptions();
    this.updateMasterId();
  }

  getVariantData() {
    const element = this.querySelector('script[type="application/json"]');
    if (!element) return [];
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('Unable to parse product variants.', error);
      return [];
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) =>
      variant.options.every((option, index) => this.options[index] === option)
    );
  }

  async onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.setUnavailable();
      return;
    }

    this.updateVariantInputs();
    this.updateURL();
    this.updateMedia();
    this.toggleAddButton(true, '');
    await this.renderProductInfo();
  }

  getSectionElement(id) {
    return this.sectionRoot.querySelector(`#${CSS.escape(id)}`) || document.getElementById(id);
  }

  getProductForms() {
    const selectors = [
      `#ProductForm-${this.sectionId}`,
      `#product-form-${this.sectionId}`,
      `#product-form-installment-${this.sectionId}`,
      `#FeaturedProductForm-${this.sectionId}`
    ];
    return selectors
      .map((selector) => document.querySelector(selector))
      .filter(Boolean);
  }

  updateVariantInputs() {
    this.getProductForms().forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateURL() {
    if (this.dataset.updateUrl === 'false' || !this.currentVariant?.id) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateMedia() {
    const mediaId = this.currentVariant?.featured_media?.id;
    if (!mediaId) return;

    const gallery = this.sectionRoot.querySelector('.product-gallery');
    if (!gallery) return;

    const target = gallery.querySelector(`.product-gallery__slide[data-media-id="${mediaId}"]`);
    if (target) {
      gallery.querySelectorAll('.product-gallery__slide').forEach((item) => {
        item.classList.toggle('is-active', item === target);
      });
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    gallery.querySelectorAll('.product-gallery__thumb').forEach((thumb) => {
      const active = thumb.dataset.targetMediaId === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  async renderProductInfo() {
    if (!this.dataset.url) {
      this.updatePriceDirect();
      this.toggleAddButton(!this.currentVariant.available, this.currentVariant.available ? '' : 'Sold out');
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('variant', this.currentVariant.id);
    url.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(url.toString(), { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      this.replaceSectionContent(html, `price-${this.sectionId}`);
      this.replaceSectionContent(html, `availability-${this.sectionId}`);
      this.replaceSectionContent(html, `StoreAvailability-${this.sectionId}`);
      this.replaceSectionContent(html, `ProductSku-${this.sectionId}`);

      this.toggleAddButton(
        !this.currentVariant.available,
        this.currentVariant.available ? '' : 'Sold out'
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.updatePriceDirect();
      this.toggleAddButton(!this.currentVariant.available, this.currentVariant.available ? '' : 'Sold out');
    }
  }

  replaceSectionContent(html, id) {
    const source = html.getElementById(id);
    const target = this.getSectionElement(id);
    if (!source || !target) return;
    target.replaceWith(source);
    if (id.startsWith('StoreAvailability-')) this.initializePickupAvailability(source);
  }

  initializePickupAvailability(container) {
    const toggle = container.querySelector('.store-availability__toggle');
    const list = container.querySelector('.store-availability__list');
    if (!toggle || !list) return;
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      list.hidden = expanded;
    });
  }

  updatePriceDirect() {
    const priceElement = this.getSectionElement(`price-${this.sectionId}`);
    if (!priceElement || !this.currentVariant) return;
    const formatter = window.Visionary?.formatMoney || ((value) => `${(value / 100).toFixed(2)}`);
    const price = formatter(this.currentVariant.price);
    const compareAtPrice = this.currentVariant.compare_at_price;
    priceElement.innerHTML = compareAtPrice && compareAtPrice > this.currentVariant.price
      ? `<div class="price price--on-sale"><span class="price-item price-item--sale">${price}</span><s class="price-item price-item--regular">${formatter(compareAtPrice)}</s></div>`
      : `<div class="price"><span class="price-item price-item--regular">${price}</span></div>`;
  }

  toggleAddButton(disabled, text) {
    this.getProductForms().forEach((form) => {
      const button = form.querySelector('button[name="add"], [type="submit"]');
      if (!button) return;
      const textElement = button.querySelector('[data-btn-text]') || button;
      button.toggleAttribute('disabled', disabled);
      if (text) textElement.textContent = text;
      if (!disabled && !text) {
        textElement.textContent = button.dataset.originalText || 'Add to cart';
      }
    });

    const sticky = this.getSectionElement(`StickyAtc-${this.sectionId}`);
    const stickyButton = sticky?.querySelector('[data-sticky-atc-btn]');
    if (stickyButton) stickyButton.toggleAttribute('disabled', disabled);
  }

  removeErrorMessage() {
    this.sectionRoot.querySelector('.product-form__error')?.setAttribute('hidden', '');
  }

  setUnavailable() {
    this.toggleAddButton(true, 'Unavailable');
    const priceElement = this.getSectionElement(`price-${this.sectionId}`);
    if (priceElement) priceElement.setAttribute('hidden', '');
    const availabilityElement = this.getSectionElement(`availability-${this.sectionId}`);
    if (availabilityElement) availabilityElement.textContent = 'Unavailable';
  }
}

class VariantRadios extends VariantSelects {
  updateOptions() {
    this.options = Array.from(this.querySelectorAll('fieldset'), (fieldset) => {
      return fieldset.querySelector('input[type="radio"]:checked')?.value || '';
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateOptionAvailability();
  }

  async onVariantChange() {
    await super.onVariantChange();
    this.updateOptionAvailability();
  }

  updateOptionAvailability() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    fieldsets.forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const candidateOptions = [...this.options];
        candidateOptions[optionIndex] = input.value;
        const matchingVariants = this.variantData.filter((variant) =>
          variant.options.every((option, index) => !candidateOptions[index] || candidateOptions[index] === option)
        );
        const available = matchingVariants.some((variant) => variant.available);
        input.classList.toggle('disabled', !available);
        input.setAttribute('aria-disabled', available ? 'false' : 'true');
        input.nextElementSibling?.classList.toggle('sold-out', !available);
      });
    });
  }
}

if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);
if (!customElements.get('variant-radios')) customElements.define('variant-radios', VariantRadios);
